/**
 * Public creator/affiliate application form for The Prima Club.
 * Standalone page (no CRM chrome) - link is shared on socials and the site.
 * Posts to the public endpoint, which creates the CRM contact + application
 * and notifies the owners.
 *
 * NOTE: deliberately does NOT use the shared CRM ui components (Input, Label,
 * Button) - those are styled for the dark CRM and are unreadable on this
 * light standalone page. All styling is local.
 */
import { useState } from "react";
import { Sparkles } from "lucide-react";

interface FormState {
  first_name: string;
  last_name: string;
  email: string;
  social_links: string;
  previous_customer: string;
  follows_social: string;
  dance_context: string;
  notes: string;
}

const INITIAL: FormState = {
  first_name: "",
  last_name: "",
  email: "",
  social_links: "",
  previous_customer: "",
  follows_social: "",
  dance_context: "",
  notes: "",
};

const controlClass =
  "w-full rounded-lg border border-[#d4d4d8] bg-white px-3 py-2 text-sm text-[#18181b] placeholder:text-[#a1a1aa] shadow-sm transition-colors focus:border-[#fb7185] focus:outline-none focus:ring-2 focus:ring-[#fecdd3]";

export function CreatorApplicationPage() {
  const [form, setForm] = useState<FormState>(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  function set<K extends keyof FormState>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    // Required fields (mirrors the * questions on the original form)
    if (!form.first_name.trim() || !form.last_name.trim()) return setError("Please enter your first and last name.");
    if (!form.email.trim()) return setError("Please enter your email address.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return setError("That email address doesn't look right.");
    if (!form.social_links.trim()) return setError("Please share at least one social media link.");
    if (!form.previous_customer) return setError("Please tell us if you've shopped with us before.");
    if (!form.follows_social) return setError("Please tell us if you follow us on Instagram and TikTok.");

    setSubmitting(true);
    try {
      const res = await fetch("/api/public/affiliates/apply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(data.error ?? "Something went wrong. Please try again.");
      }
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#fff1f2] via-white to-white">
      <div className="mx-auto max-w-2xl px-4 py-10 sm:py-16">
        {done ? (
          <div className="rounded-2xl border border-[#ffe4e6] bg-white p-8 text-center shadow-sm">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#ffe4e6]">
              <Sparkles className="h-7 w-7 text-[#b76e79]" />
            </div>
            <h1 className="mt-4 text-2xl font-semibold tracking-tight text-[#18181b]">Thank you for applying!</h1>
            <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-[#52525b]">
              THANK YOU FOR YOUR INTEREST IN JOINING THE PRIMA CLUB!!
              <br />
              Applications are reviewed on a rolling basis. Approved creators will be contacted by email.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="space-y-6">
            <header className="space-y-2 text-center">
              <h1 className="text-3xl font-semibold tracking-tight text-[#18181b]">The Prima Club Creator Application</h1>
              <p className="text-sm leading-relaxed text-[#52525b]">
                The Prima Club Creators program is for community members who love the brand and want to create
                authentic content with us and get some fun perks!
              </p>
            </header>

            <div className="space-y-5 rounded-2xl border border-[#e4e4e7] bg-white p-6 shadow-sm shadow-[#e4e4e7]/50 sm:p-8">
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="First Name" required>
                  <input className={controlClass} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} maxLength={80} autoComplete="given-name" />
                </Field>
                <Field label="Last Name" required>
                  <input className={controlClass} value={form.last_name} onChange={(e) => set("last_name", e.target.value)} maxLength={80} autoComplete="family-name" />
                </Field>
              </div>
              <Field label="Email address" required>
                <input type="email" className={controlClass} value={form.email} onChange={(e) => set("email", e.target.value)} maxLength={200} autoComplete="email" />
              </Field>
              <Field label="Social Media Links (Instagram, Tiktok, YouTube)" required hint="Paste one or more links, separated by commas">
                <textarea
                  className={`${controlClass} min-h-[90px] resize-y`}
                  value={form.social_links}
                  onChange={(e) => set("social_links", e.target.value)}
                  maxLength={2000}
                  placeholder="https://instagram.com/... , https://tiktok.com/..."
                />
              </Field>
              <Field label="Are you a previous Prima Club customer?" required>
                <YesNo value={form.previous_customer} onChange={(v) => set("previous_customer", v)} name="previous-customer" />
              </Field>
              <Field label="Where do you dance currently? Professionally or just for fun?">
                <textarea
                  className={`${controlClass} min-h-[70px] resize-y`}
                  value={form.dance_context}
                  onChange={(e) => set("dance_context", e.target.value)}
                  maxLength={2000}
                />
              </Field>
              <Field label="Do you follow us on Instagram and TikTok?" required>
                <YesNo value={form.follows_social} onChange={(v) => set("follows_social", v)} name="follows-social" />
              </Field>
              <Field label="Anything else we should know?">
                <textarea
                  className={`${controlClass} min-h-[70px] resize-y`}
                  value={form.notes}
                  onChange={(e) => set("notes", e.target.value)}
                  maxLength={2000}
                />
              </Field>

              {error && (
                <p className="rounded-lg border border-[#fecaca] bg-[#fef2f2] px-3 py-2 text-sm text-[#b91c1c]">{error}</p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="flex h-11 w-full cursor-pointer items-center justify-center rounded-lg bg-[#f0ccd1] text-sm font-semibold text-[#4a2f36] shadow-sm transition-colors hover:bg-[#e8bcc4] active:bg-[#e2adb8] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? "Submitting..." : "Submit application"}
              </button>
            </div>

            <p className="text-center text-xs leading-relaxed text-[#71717a]">
              THANK YOU FOR YOUR INTEREST IN JOINING THE PRIMA CLUB!!
              <br />
              Applications are reviewed on a rolling basis. Approved creators will be contacted by email.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-[#3f3f46]">
        {label}
        {required && <span className="ml-0.5 text-[#e11d48]">*</span>}
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-[#71717a]">{hint}</p>}
    </div>
  );
}

function YesNo({ value, onChange, name }: { value: string; onChange: (v: string) => void; name: string }) {
  return (
    <div className="flex gap-6 pt-1">
      {[
        ["Yes", "Yes"],
        ["No", "No"],
      ].map(([label, val]) => (
        <label key={val} className="inline-flex cursor-pointer items-center gap-2 text-sm text-[#3f3f46]">
          <input
            type="radio"
            name={name}
            checked={value === val}
            onChange={() => onChange(val)}
            className="h-4 w-4 accent-[#b76e79]"
          />
          {label}
        </label>
      ))}
    </div>
  );
}
